import { useQuery } from "@tanstack/react-query";
import React, {
  createContext,
  useEffect,
  useContext,
  useState,
  type Dispatch,
  type SetStateAction,
} from "react";
import { apiRequest } from "shared/api/apiRequest";
import { getSocietyProfileReportIdContract } from "shared/api/contracts/society/societyId/profiles/profileId/reports/reportId";
import { useCustomSession } from "shared/context/CustomSessionContext";
import { ReviewInstanceAssignmentView } from "shared/mappers/database/review/reviewInstanceAssignment";
import { ReviewSessionView } from "shared/mappers/database/review/reviewSession";
import { HubReviewsRouteParams } from "shared/types/HubPageRouteParams";

export type ReviewAssignmentProgress = {
  nextInstanceId?: number;
  previousInstanceId?: number;
  firstUnscoredInstanceId?: number;
};

export type ReviewSessionExtended = ReviewSessionView & {
  progress?: ReviewAssignmentProgress;
};

type HubReviewsContextType = {
  reviewInstanceReportProfileId: number | undefined;
  setReviewInstanceReportProfileId: Dispatch<
    SetStateAction<number | undefined>
  >;
  setCurrentInstanceId: Dispatch<SetStateAction<number | undefined>>;
  reviewSessionReportProfileId: number | undefined;
  setReviewSessionReportProfileId: Dispatch<SetStateAction<number | undefined>>;
  singleReviewInstanceReportProfileId: number | undefined;
  setSingleReviewInstanceReportProfileId: Dispatch<
    SetStateAction<number | undefined>
  >;
  reviewSessions: ReviewSessionExtended[];
  reviewSessionDataIsLoading: boolean;
};

export const HubReviewsContext = createContext<
  HubReviewsContextType | undefined
>(undefined);

export const useHubReviewsContext = ({
  reviewInstanceReportProfileId,
  reviewSessionReportProfileId,
  singleReviewInstanceReportProfileId,
}: HubReviewsRouteParams = {}) => {
  const context = useContext(HubReviewsContext);

  if (!context) {
    throw new Error(
      "useHubReviewsContext must be used within a HubReviewsContextProvider",
    );
  }

  useEffect(() => {
    if (
      reviewInstanceReportProfileId ||
      reviewSessionReportProfileId ||
      singleReviewInstanceReportProfileId
    ) {
      context.setReviewInstanceReportProfileId(reviewInstanceReportProfileId);
      context.setReviewSessionReportProfileId(reviewSessionReportProfileId);
      context.setSingleReviewInstanceReportProfileId(
        singleReviewInstanceReportProfileId,
      );
    }
  }, []);

  return context;
};

export const HubReviewsContextProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const { societyId, profileId } = useCustomSession();

  const [currentInstanceId, setCurrentInstanceId] = useState<
    number | undefined
  >(undefined);

  const [reviewSessionReportProfileId, setReviewSessionReportProfileId] =
    useState<number | undefined>(undefined);

  const [reviewInstanceReportProfileId, setReviewInstanceReportProfileId] =
    useState<number | undefined>(undefined);

  const [
    singleReviewInstanceReportProfileId,
    setSingleReviewInstanceReportProfileId,
  ] = useState<number | undefined>(undefined);

  const { data: reviewSessionData, isLoading: reviewSessionDataIsLoading } =
    useQuery({
      queryKey: ["reviewSessions", reviewSessionReportProfileId],
      enabled: !!societyId && !!profileId && !!reviewSessionReportProfileId,
      queryFn: async () => {
        if (!societyId || !profileId || !reviewSessionReportProfileId)
          return null;

        const responseData = await apiRequest({
          contract: getSocietyProfileReportIdContract,
          inputs: {
            params: {
              societyId: societyId!,
              reportId: reviewSessionReportProfileId!,
              profileId: profileId!,
            },
          },
        });

        if (!responseData?.results?.length) return null;
        return responseData;
      },
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
    });

  const reviewSessionResults =
    reviewSessionData?.results as unknown as ReviewSessionView[];

  const reviewSessions: ReviewSessionExtended[] | undefined =
    reviewSessionResults?.map((session: ReviewSessionView) => {
      const firstUnscoredReviewAssignment =
        session.reviewInstanceAssignment?.find((ria) => !ria.vote?.length) ??
        session.reviewInstanceAssignment![0]!;

      const { nextInstanceId, previousInstanceId } =
        getReviewSessionAssignmentNavigationIds({
          assignments: session?.reviewInstanceAssignment ?? [],
          currentInstanceId: currentInstanceId!,
        });

      return {
        ...session,
        progress: {
          nextInstanceId: nextInstanceId!,
          previousInstanceId: previousInstanceId!,
          firstUnscoredInstanceId:
            firstUnscoredReviewAssignment.reviewInstance?.reviewInstanceId,
        },
      };
    });

  const showCharts = reviewSessionResults?.some((session) => session.showChart);

  return (
    <HubReviewsContext.Provider
      value={{
        reviewSessions: reviewSessions!,
        reviewInstanceReportProfileId,
        setReviewInstanceReportProfileId,
        setCurrentInstanceId,
        reviewSessionReportProfileId,
        setReviewSessionReportProfileId,
        singleReviewInstanceReportProfileId,
        setSingleReviewInstanceReportProfileId,
        reviewSessionDataIsLoading,
      }}
    >
      <div className={`${showCharts && "grid-cols-9"} grid pb-4`}>
        {children}
      </div>
    </HubReviewsContext.Provider>
  );
};

const getReviewSessionAssignmentNavigationIds = ({
  assignments,
  currentInstanceId,
}: {
  assignments: ReviewInstanceAssignmentView[];
  currentInstanceId: number;
}) => {
  const index = assignments.findIndex(
    (a) => a.reviewInstance?.reviewInstanceId === currentInstanceId,
  );

  return {
    previousInstanceId:
      assignments[index - 1]?.reviewInstance?.reviewInstanceId ?? null,
    nextInstanceId:
      assignments[index + 1]?.reviewInstance?.reviewInstanceId ?? null,
  };
};
